import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useReassignInviteMutation } from 'src/api/mutations/userManagementMutation';
import { useAlerts } from 'src/common/AlertManager';
import { Button, Input } from 'src/components';
import BaseModal from 'src/components/BaseModal';
import { BaseModalProps } from 'src/components/BaseModal/BaseModal';
import useErrorMessages from 'src/hooks/useErrorsMessage';
import useModal from 'src/hooks/useModal';

import styles from '../styles.module.scss';
import messages from '../messages';

interface ReassignInviteModalProps extends BaseModalProps {
  user: any;
  onReassigned: () => void;
}

/**
 * Hand an UNACCEPTED invitation to a different person — plan-structure v2
 * Phase 7a. The seat is already paid for and the invitee never signed in, so
 * swapping the address is safe; the backend mints a fresh invite code (killing
 * the link already sent) and emails the new address.
 *
 * Only offered on pending invites: re-pointing an activated account would hand
 * the new person the previous member's assets, API key and history.
 */
const ReassignInviteModal = ({ user, onReassigned, ...rest }: ReassignInviteModalProps) => {
  const { formatMessage } = useIntl();
  const { showSuccess, showAlert } = useAlerts();
  const { getErrorMessage } = useErrorMessages();
  const { closeModal } = useModal();
  const [email, setEmail] = useState('');

  const reassignMutation = useReassignInviteMutation(user?.id ?? -1, {
    onSuccess: () => {
      showSuccess(formatMessage(messages.reassignInviteSuccess, { email }));
      closeModal();
      onReassigned();
    },
    onError: (error: any) => showAlert(getErrorMessage(error)),
  });

  if (!user) return null;

  const emailIsValid = /.+@.+\..+/.test(email.trim());

  return (
    <BaseModal {...rest}>
      <h3>
        <FormattedMessage {...messages.reassignInviteTitle} />
      </h3>
      <p>
        <FormattedMessage {...messages.reassignInviteBody} values={{ email: user.email }} />
      </p>
      <Input
        className={styles.formSpacing}
        label={formatMessage(messages.newEmail)}
        value={email}
        onChange={setEmail}
      />
      <div className={styles.groupedButtons}>
        <Button
          mode="primary"
          size="small"
          disabled={!emailIsValid}
          loading={reassignMutation.isLoading}
          onClick={() => reassignMutation.mutate(email.trim())}
        >
          <FormattedMessage {...messages.reassignInvite} />
        </Button>
        <Button mode="tertiary" size="small" onClick={closeModal} disabled={reassignMutation.isLoading}>
          <FormattedMessage {...messages.cancel} />
        </Button>
      </div>
    </BaseModal>
  );
};

export default ReassignInviteModal;
